import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminRole } from "@/lib/utils";

const OAUTH_ERROR_COPY = {
  google_cancelled:
    "Google sign-in was cancelled. You can try again or use email sign-in.",
  google_auth_failed:
    "We couldn't finish Google sign-in. Please try again.",
};

export default function LoginPage() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Surface OAuth errors carried back on ?error=… by our backend.
  useEffect(() => {
    const code = searchParams.get("error");
    if (!code) return;
    const msg = OAUTH_ERROR_COPY[code] || OAUTH_ERROR_COPY.google_auth_failed;
    toast.error(msg, { id: `oauth-${code}` });
    // Strip the query param so refreshing the page doesn't repeat the toast.
    const next = new URLSearchParams(searchParams);
    next.delete("error");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email.trim(), password);
    setSubmitting(false);

    if (!result.ok) {
      const map = {
        INVALID_CREDENTIALS: "That email and password don't match our records.",
        USE_GOOGLE_SIGNIN:
          "This email is linked to Google. Please use Continue with Google below.",
        RATE_LIMITED:
          "Too many attempts. Please try again in a few minutes.",
      };
      setError(map[result.error] || result.error);
      return;
    }

    // Admin accounts are NOT permitted to sign in via the public /login.
    // Log the session out immediately and surface the same generic error
    // brokers see for bad passwords — leaks zero role signal.
    if (isAdminRole(result.user.role)) {
      logout();
      setError("That email and password don't match our records.");
      return;
    }

    const from = location.state?.from;
    if (result.user.profile_incomplete) {
      navigate("/complete-profile", { replace: true });
    } else if (!result.user.is_verified) {
      navigate("/verification-pending", { replace: true });
    } else {
      navigate(from && from !== "/login" ? from : "/dashboard", { replace: true });
    }
  };

  return (
    <div className="mb-page grid min-h-screen grid-cols-1 md:grid-cols-12">
      {/* Left form panel */}
      <div className="col-span-1 flex items-center justify-center p-6 md:col-span-6 md:p-12">
        <div className="w-full max-w-md mb-rise">
          <Link to="/login" className="mb-focus-ring mb-10 inline-flex items-center rounded-lg">
            <BrandLogo className="h-10" />
          </Link>

          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Welcome back.
          </h1>
          <p className="mt-3 text-base leading-relaxed text-gray-500">
            Sign in to your verified broker account.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-gray-700">
                Email
              </Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@matchbroker.ph"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="login-password" className="text-gray-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white pr-12 text-base"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-700"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="mb-focus-ring h-12 w-full rounded-xl bg-[#1B2B4B] text-base font-semibold text-white shadow-sm transition hover:bg-[#14213A] hover:shadow-md disabled:opacity-70"
              data-testid="login-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>

            <div className="pt-1 text-right">
              <Link
                to="/forgot-password"
                className="mb-focus-ring text-xs font-medium text-gray-500 hover:text-gray-900"
                data-testid="login-forgot-password"
              >
                Forgot password?
              </Link>
            </div>

            <div className="relative py-2" data-testid="login-divider">
              <div className="absolute inset-0 flex items-center">
                <span className="h-px w-full bg-gray-100" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                  or
                </span>
              </div>
            </div>

            <GoogleSignInButton testId="login-google-button" />

            <p className="pt-2 text-sm text-gray-500">
              New to MatchBroker?{" "}
              <Link
                to="/register"
                className="font-semibold text-[#1B2B4B] hover:underline"
                data-testid="login-goto-register"
              >
                Create a broker account
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right visual panel — logo removed to avoid duplicate lockup;
          intentionally shows only the tagline + trust chips. */}
      <aside className="relative hidden overflow-hidden bg-white md:col-span-6 md:block">
        <div className="absolute inset-0 mb-grain" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-navy/70">
            For verified PH brokers
          </div>
          <div>
            <div className="h-px w-16 bg-brand-navy/20" />
            <h2 className="mt-8 text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
              Exclusive Listing &amp; Matching Platform for Verified PH Brokers
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-gray-500">
              Every broker is PRC-verified. Every listing has an owner.
              MatchBroker connects your inventory to real buyer intent —
              simple and efficient, without the noise.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["PRC verified", "Exclusive ATS", "AI matching", "Co-broke deals"].map((t) => (
                <span
                  key={t}
                  className="mb-transition inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-brand-navy/40"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-gray-400">
            Metro Manila · Cebu · Davao · OFW community
          </div>
        </div>
      </aside>
    </div>
  );
}
